import { useState, useRef, useEffect, useCallback } from 'react';
import { cn } from '@/lib/utils';

interface PhotoComparisonSliderProps {
  originalSrc: string;
  enhancedSrc: string;
  className?: string;
  aspectRatio?: string;
}

export function PhotoComparisonSlider({ originalSrc, enhancedSrc, className, aspectRatio }: PhotoComparisonSliderProps) {
  const [sliderPosition, setSliderPosition] = useState(50);
  const containerRef = useRef<HTMLDivElement>(null);
  const isDragging = useRef(false);
  const activePointerId = useRef<number | null>(null);

  // Reset slider position when images change
  useEffect(() => {
    setSliderPosition(50);
  }, [originalSrc, enhancedSrc]);

  const handleMove = useCallback((clientX: number) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const x = clientX - rect.left;
    const percentage = Math.max(0, Math.min(100, (x / rect.width) * 100));
    setSliderPosition(percentage);
  }, []);

  const handlePointerDown = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    // Only main button for mouse; allow touch/pen always
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    e.preventDefault();
    isDragging.current = true;
    activePointerId.current = e.pointerId;
    try {
      (e.currentTarget as HTMLDivElement).setPointerCapture(e.pointerId);
    } catch {/* no-op */}
    handleMove(e.clientX);
  }, [handleMove]);

  const handlePointerMove = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (!isDragging.current) return;
    if (activePointerId.current !== null && e.pointerId !== activePointerId.current) return;
    handleMove(e.clientX);
  }, [handleMove]);

  const handlePointerEnd = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (activePointerId.current !== null && e.pointerId !== activePointerId.current) return;
    isDragging.current = false;
    activePointerId.current = null;
    try {
      (e.currentTarget as HTMLDivElement).releasePointerCapture(e.pointerId);
    } catch {/* no-op */}
  }, []);

  return (
    <div
      ref={containerRef}
      className={cn(
        "relative overflow-hidden rounded-xl select-none w-full",
        aspectRatio || "aspect-[4/3]",
        className
      )}
      style={{ touchAction: 'pan-y' }}
    >
      {/* Enhanced (background) */}
      <img
        src={enhancedSrc}
        alt="AI enhanced property photo"
        className="absolute inset-0 w-full h-full object-cover block pointer-events-none"
        draggable={false}
        width={1280}
        height={960}
        fetchPriority="high"
        decoding="async"
        loading="eager"
      />
      
      {/* Original (clipped overlay) */}
      <div
        className="absolute inset-0 overflow-hidden pointer-events-none w-full h-full"
        style={{ clipPath: `inset(0 ${100 - sliderPosition}% 0 0)` }}
      >
        <img
          src={originalSrc}
          alt="Original property photo before enhancement"
          className="w-full h-full object-cover block"
          draggable={false}
          width={1280}
          height={960}
          decoding="async"
          loading="eager"
        />
      </div>

      {/* Drag area — unified pointer events handle mouse, touch, and pen reliably */}
      <div
        className="absolute inset-0 cursor-ew-resize z-50"
        style={{ touchAction: 'none' }}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerEnd}
        onPointerCancel={handlePointerEnd}
      />

      {/* Slider handle */}
      <div
        className="absolute top-0 bottom-0 w-1 bg-white shadow-lg z-[60] pointer-events-none"
        style={{ left: `${sliderPosition}%`, transform: 'translateX(-50%)' }}
      >
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-12 h-12 min-w-[48px] min-h-[48px] bg-white rounded-full shadow-lg flex items-center justify-center cursor-ew-resize">
          <div className="flex gap-0.5">
            <div className="w-0.5 h-4 bg-muted-foreground rounded-full" />
            <div className="w-0.5 h-4 bg-muted-foreground rounded-full" />
          </div>
        </div>
      </div>

      {/* Labels */}
      <div className="absolute top-3 left-3 px-2 py-1 bg-background/80 backdrop-blur-sm rounded text-xs font-medium pointer-events-none z-30">
        Original
      </div>
      <div className="absolute top-3 right-3 px-2 py-1 bg-primary/90 backdrop-blur-sm rounded text-xs font-medium text-primary-foreground pointer-events-none z-30">
        Enhanced
      </div>
    </div>
  );
}